'use client';

import { useMaybeRoomContext } from '@livekit/components-react';
import JSZip from 'jszip';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  clearAllRecordings,
  deleteRecording as deleteDbRecording,
  getRecordings,
  Recording,
} from './db';
import { getStorageEstimate } from './storage-utils';

// --- Icone per i pulsanti ---
const DownloadIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>;
const DeleteIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>;
const ZipIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>;
const ClearIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18m-2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>;

function formatDuration(ms: number) {
    const seconds = Math.floor((ms / 1000) % 60);
    const minutes = Math.floor((ms / (1000 * 60)) % 60);
    const pad = (num: number) => num.toString().padStart(2, '0');
    return `${pad(minutes)}:${pad(seconds)}`;
}

function formatBytes(bytes: number, decimals = 2) {
  if (!+bytes) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

function useStorageEstimate(updateTrigger: number) {
    const [estimate, setEstimate] = useState<{usage: number, quota: number} | null>(null);
    useEffect(() => {
        getStorageEstimate().then(setEstimate);
    }, [updateTrigger]);
    return estimate;
}

export function RecordingHistory({ currentRoomId: currentRoomIdProp }: { currentRoomId?: string }) {
    const room = useMaybeRoomContext(); // Utilizza useMaybeRoomContext per evitare errori
    const currentRoomId = currentRoomIdProp ?? room?.name;
    
    const [recordings, setRecordings] = useState<Recording[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isZipping, setIsZipping] = useState(false);
    const [activeTab, setActiveTab] = useState<'current' | 'previous'>('current');
    const [storageUpdateKey, setStorageUpdateKey] = useState(0);
    const storageEstimate = useStorageEstimate(storageUpdateKey);

    const triggerUpdate = useCallback(() => {
        setStorageUpdateKey(prev => prev + 1);
        fetchRecordings();
    }, []);

    const fetchRecordings = useCallback(async () => {
        setIsLoading(true);
        try {
            const recs = await getRecordings();
            setRecordings(recs);
        } catch (error) {
            console.error("Failed to load recordings", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRecordings();
        window.addEventListener('storage', triggerUpdate);
        return () => window.removeEventListener('storage', triggerUpdate);
    }, [fetchRecordings, triggerUpdate]);

    const { currentCallRecordings, previousCallRecordings, allRecordings } = useMemo(() => {
        if (currentRoomId) {
            return {
                currentCallRecordings: recordings.filter(r => r.roomId === currentRoomId),
                previousCallRecordings: recordings.filter(r => r.roomId !== currentRoomId),
                allRecordings: [],
            };
        }
        return {
            currentCallRecordings: [],
            previousCallRecordings: [],
            allRecordings: recordings,
        };
    }, [recordings, currentRoomId]);

    const handleDelete = async (id: string) => {
        if (window.confirm("Sei sicuro di voler eliminare questa registrazione?")) {
            await deleteDbRecording(id);
            triggerUpdate();
            toast.success("Registrazione eliminata.");
        }
    };
    
    const handleClearAll = async () => {
        if (window.confirm("Sei sicuro di voler eliminare TUTTE le registrazioni locali? L'azione è irreversibile.")) {
            await clearAllRecordings();
            triggerUpdate();
            toast.success("Cronologia registrazioni pulita.");
        }
    };

    const createZip = async (recsToZip: Recording[], name: string) => {
        if (recsToZip.length === 0) return;
        setIsZipping(true);
        toast.loading("Creazione del file zip...");
        try {
            const zip = new JSZip();
            recsToZip.forEach(rec => zip.file(rec.name, rec.blob));
            const zipBlob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(zipBlob);
            link.download = name;
            link.click();
            URL.revokeObjectURL(link.href);
            toast.dismiss(); toast.success("Download avviato.");
        } catch (error) {
            toast.dismiss(); toast.error("Impossibile creare lo zip.");
        } finally {
            setIsZipping(false);
        }
    };
    
    const recordingsToShow = currentRoomId ? (activeTab === 'current' ? currentCallRecordings : previousCallRecordings) : allRecordings;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', color: 'var(--lk-text-primary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '0.5rem'}}>
                    {currentRoomId && (
                        <ActionButton onClick={() => createZip(currentCallRecordings, `Registrazioni-${currentRoomId}.zip`)} disabled={isZipping || currentCallRecordings.length === 0} title='Scarica registrazioni della chiamata attuale'>
                           <ZipIcon /> <span style={{marginLeft: '6px'}}>Attuali</span>
                        </ActionButton>
                    )}
                    <ActionButton onClick={() => createZip(recordings, 'Tutte-le-Registrazioni.zip')} disabled={isZipping || recordings.length === 0} title='Scarica tutte le registrazioni'>
                        <ZipIcon /> <span style={{marginLeft: '6px'}}>Tutte</span>
                    </ActionButton>
                </div>
                <ActionButton isDanger onClick={handleClearAll} disabled={recordings.length === 0} title="Pulisci tutta la cronologia">
                    <ClearIcon />
                </ActionButton>
            </div>
            
            {currentRoomId && (
                 <div style={{ display: 'flex', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', padding: '4px' }}>
                    <TabButton isActive={activeTab === 'current'} onClick={() => setActiveTab('current')}>Attuale ({currentCallRecordings.length})</TabButton>
                    <TabButton isActive={activeTab === 'previous'} onClick={() => setActiveTab('previous')}>Precedenti ({previousCallRecordings.length})</TabButton>
                </div>
            )}

            <div style={{ height: '220px', overflowY: 'auto', position: 'relative' }}>
                {isLoading ? <p>Caricamento...</p> : recordingsToShow.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#a7a7a7', marginTop: '3rem', fontSize: '0.9em' }}>Nessuna registrazione.</p>
                ) : (
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {recordingsToShow.map((rec) => (
                            <RecordingItem key={rec.id} recording={rec} onDelete={() => handleDelete(rec.id)} onDownload={() => { const url = URL.createObjectURL(rec.blob); const a = document.createElement('a'); a.href = url; a.download = rec.name; a.click(); URL.revokeObjectURL(url); }}/>
                        ))}
                    </ul>
                )}
            </div>
            
            {storageEstimate && <StorageBar usage={storageEstimate.usage} quota={storageEstimate.quota} />}
        </div>
    );
}

const ActionButton = ({ children, isDanger = false, ...props }: { children: React.ReactNode, isDanger?: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>) => (
    <button {...props} className={isDanger ? "lk-button lk-button-danger" : "lk-button"}>{children}</button>
);

const TabButton = ({ children, isActive, ...props }: { children: React.ReactNode, isActive: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>) => (
    <button {...props} style={{ flex: 1, padding: '6px 10px', border: 'none', background: isActive ? 'var(--lk-accent-bg)' : 'transparent', color: isActive ? 'var(--lk-accent-text)' : 'var(--lk-text-primary)', cursor: 'pointer', borderRadius: '4px', fontWeight: 'bold', transition: 'background-color 0.2s ease-in-out' }}>
        {children}
    </button>
);

const RecordingItem = ({ recording, onDownload, onDelete }: { recording: Recording, onDownload: () => void, onDelete: () => void }) => (
    <li style={{ display: 'flex', alignItems: 'center', padding: '10px 4px', borderBottom: '1px solid var(--lk-bg-2)' }}>
        <div style={{ flexGrow: 1, marginRight: '8px', overflow: 'hidden' }}>
            <div style={{ fontWeight: 'bold', fontSize: '0.9em', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }} title={recording.name}>{recording.name}</div>
            <div style={{ fontSize: '0.8em', color: '#a7a7a7', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>{formatDuration(recording.duration)}</span>
                <span style={{ margin: '0 4px' }}>|</span>
                <span style={{ fontWeight: 'bold' }}>{formatBytes(recording.size)}</span>
                <span style={{ margin: '0 4px' }}>|</span>
                <span style={{ fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`ID Stanza: ${recording.roomId}`}>
                    {recording.roomId}
                </span>
            </div>
        </div>
        <div className='lk-button-group'>
            <ActionButton onClick={onDownload} title="Scarica"><DownloadIcon /></ActionButton>
            <ActionButton isDanger onClick={onDelete} title="Elimina"><DeleteIcon /></ActionButton>
        </div>
    </li>
);

const StorageBar = ({ usage, quota }: { usage: number, quota: number }) => {
    const percentage = quota > 0 ? (usage / quota) * 100 : 0;
    const barColor = percentage > 90 ? 'var(--lk-danger-light)' : percentage > 70 ? 'var(--lk-warning-light)' : 'var(--lk-accent-bg)';
    return (
        <div style={{ marginTop: 'auto', paddingTop: '0.5rem' }}>
            <div style={{ fontSize: '0.8em', color: '#ccc', marginBottom: '4px', display: 'flex', justifyContent: 'space-between' }}>
                <span>Archiviazione Locale</span>
                <span>{usage.toFixed(1)} / {quota.toFixed(1)} MB</span>
            </div>
            <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                <div style={{ width: `${percentage}%`, background: barColor, height: '100%', borderRadius: '4px', transition: 'width 0.3s' }} />
            </div>
            {percentage > 95 && <p style={{color: 'var(--lk-danger)', fontSize: '0.8em', textAlign: 'center', marginTop: '4px'}}>Spazio quasi esaurito. Libera spazio per salvare nuove registrazioni.</p>}
        </div>
    );
};
